import * as React from 'react';
import clsx from 'clsx';
import { Tabs as BaseTabs } from '@base-ui-components/react/tabs';

import { useResizeObserver } from '@strava/react-hooks/use-resize-observer';

import styles from './Tabs.module.scss';

// Anatomy
// <Tabs>
//   <Tabs.List>
//     <Tabs.Tab/>
//     <Tabs.Indicator/>
//   </Tabs.List>
//   <Tabs.Panel/>
// </Tabs>

/**
 * =====================================
 * Tabs (Root)
 *
 * Groups the tabs and the corresponding
 * panels. Renders a <div> element.
 * =====================================
 * */
export const Tabs = ({ children, className, ...props }: Tabs.Props) => {
  return (
    <BaseTabs.Root className={clsx(styles.tabs, className)} {...props}>
      {children}
    </BaseTabs.Root>
  );
};
Tabs.displayName = 'Tabs';

export namespace Tabs {
  export type Props = Omit<BaseTabs.Root.Props, 'orientation'>;

  // sub-component props
  export type TabProps = TabsTab.Props;
  export type PanelProps = TabsPanel.Props;
  export type ListProps = TabsList.Props;
}

/**
 * =====================================
 * Tabs List
 *
 * Groups the individual tab buttons.
 * Renders a <div> element.
 * =====================================
 * */
export const TabsList = ({
  children,
  className,
  align = 'start',
  tabIndicatorProps,
  ...props
}: TabsList.Props) => {
  return (
    <BaseTabs.List
      className={clsx(styles.list, className, {
        [styles.alignStart]: align === 'start',
        [styles.alignCenter]: align === 'center',
        [styles.alignEnd]: align === 'end'
      })}
      {...props}
    >
      {children}
      <TabsIndicator {...tabIndicatorProps} />
    </BaseTabs.List>
  );
};
TabsList.displayName = 'TabsList';

export namespace TabsList {
  export type Props = BaseTabs.List.Props & {
    /** The alignment of the tabs. */
    align?: 'start' | 'center' | 'end';
    /** Base ui Indicator props */
    tabIndicatorProps?: TabsIndicator.Props;
  };
}

/**
 * =====================================
 * Tab
 *
 * An individual interactive tab button
 * that toggles the corresponding panel.
 * Renders a <button> element.
 * =====================================
 * */
export const TabsTab = ({ children, className, ...props }: TabsTab.Props) => {
  return (
    <BaseTabs.Tab className={clsx(styles.tab, className)} {...props}>
      {children}
    </BaseTabs.Tab>
  );
};

TabsTab.displayName = 'TabsTab';

export namespace TabsTab {
  export type Props = BaseTabs.Tab.Props;
}

/**
 * =====================================
 * Tabs Panel
 *
 * A panel displayed when the corresponding
 * tab is active. Renders a <div> element.
 * =====================================
 * */
export const TabsPanel = ({
  children,
  className,
  ...props
}: TabsPanel.Props) => {
  return (
    <BaseTabs.Panel className={clsx(styles.panel, className)} {...props}>
      {children}
    </BaseTabs.Panel>
  );
};
TabsPanel.displayName = 'TabsPanel';

export namespace TabsPanel {
  export type Props = BaseTabs.Panel.Props;
}

/**
 * =====================================
 * Tabs Indicator
 *
 * A visual indicator that can be styled
 * to match the position of the currently
 * active tab. Renders a <span> element.
 * =====================================
 * */
export const TabsIndicator = ({ className, ...props }: TabsIndicator.Props) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [_, setTriggerUpdate] = React.useState(0);

  // Hack to force re-render when the indicator is rendered in a Modal.
  // On initial render in a Modal, the indicator does not render the full width of the Tab.
  useResizeObserver(ref, (entry) => {
    if (entry.contentRect.width > 0) {
      setTriggerUpdate((prev) => prev + 1);
    }
  });

  return (
    <BaseTabs.Indicator
      ref={ref}
      className={clsx(styles.indicator, className)}
      {...props}
    />
  );
};
TabsIndicator.displayName = 'TabsIndicator';

export namespace TabsIndicator {
  export type Props = BaseTabs.Indicator.Props;
}

/**
 * =====================================
 * Compound Components
 * =====================================
 * */
Tabs.Tab = TabsTab;
Tabs.Panel = TabsPanel;
Tabs.List = TabsList;
